import React from 'react';
import { cn } from '../../lib/utils';

interface SkeletonProps {
  /** Custom className */
  className?: string;
  /** Width in tailwind classes or pixels */
  width?: string;
  /** Height in tailwind classes or pixels */
  height?: string;
  /** Variant */
  variant?: 'text' | 'circular' | 'rectangular';
}

/**
 * Skeleton loader component for loading states
 *
 * @example
 * ```tsx
 * <Skeleton className="h-4 w-32" />
 * <Skeleton variant="circular" className="h-12 w-12" />
 * ```
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  className,
  variant = 'rectangular',
}) => {
  const baseClasses = 'animate-pulse bg-gray-200';

  const variantClasses = {
    text: 'rounded h-4',
    circular: 'rounded-full',
    rectangular: 'rounded',
  };

  return (
    <div
      className={cn(
        baseClasses,
        variantClasses[variant],
        className
      )}
    />
  );
};

/**
 * Card skeleton for loading campaign cards
 */
export const SkeletonCard: React.FC = () => {
  return (
    <div className="card p-6 space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-6 w-48" />
        <Skeleton variant="rectangular" className="h-6 w-16" />
      </div>
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <div className="flex gap-2 mt-4">
        <Skeleton className="h-8 w-20" />
        <Skeleton className="h-8 w-20" />
        <Skeleton className="h-8 w-20" />
      </div>
    </div>
  );
};

/**
 * Table skeleton for loading tables
 */
export const SkeletonTable: React.FC<{ rows?: number }> = ({ rows = 5 }) => {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 p-4 border rounded">
          <Skeleton className="h-4 w-1/4" />
          <Skeleton className="h-4 w-1/4" />
          <Skeleton className="h-4 w-1/4" />
          <Skeleton className="h-4 w-1/4" />
        </div>
      ))}
    </div>
  );
};

/**
 * Stat card skeleton for loading dashboard stat cards
 */
export const SkeletonStatCard: React.FC = () => {
  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Skeleton variant="rectangular" className="h-12 w-12" />
          </div>
          <div className="ml-5 w-0 flex-1">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-8 w-16 mb-1" />
            <Skeleton className="h-3 w-32" />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Pie chart skeleton for loading charts
 */
export const SkeletonPieChart: React.FC = () => {
  return (
    <div className="bg-white shadow rounded-lg p-6">
      <Skeleton className="h-5 w-48 mb-4" />
      <div className="flex items-center justify-center">
        <Skeleton variant="circular" className="h-48 w-48" />
      </div>
      <div className="mt-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Skeleton variant="rectangular" className="h-3 w-3" />
            <Skeleton className="h-3 w-20" />
          </div>
          <Skeleton className="h-3 w-12" />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Skeleton variant="rectangular" className="h-3 w-3" />
            <Skeleton className="h-3 w-20" />
          </div>
          <Skeleton className="h-3 w-12" />
        </div>
      </div>
    </div>
  );
};

/**
 * Usage progress bar skeleton for loading usage metrics
 */
export const SkeletonUsageBar: React.FC = () => {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-24" />
      </div>
      <Skeleton className="h-2 w-full" />
    </div>
  );
};

/**
 * Recent activity skeleton for loading activity list
 */
export const SkeletonRecentActivity: React.FC<{ count?: number }> = ({ count = 5 }) => {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded">
          <div className="flex-1">
            <Skeleton className="h-4 w-40 mb-2" />
            <Skeleton className="h-3 w-56" />
          </div>
          <Skeleton variant="rectangular" className="h-6 w-16" />
        </div>
      ))}
    </div>
  );
};

/**
 * Dashboard skeleton - complete loading state for dashboard
 */
export const SkeletonDashboard: React.FC = () => {
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-40" />
      </div>

      {/* Campaign Stats */}
      <div>
        <Skeleton className="h-6 w-40 mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
        </div>
      </div>

      {/* Request Stats */}
      <div>
        <Skeleton className="h-6 w-40 mb-4" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
        </div>
      </div>

      {/* Charts and Usage */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SkeletonPieChart />
        <div className="bg-white shadow rounded-lg p-6">
          <Skeleton className="h-6 w-32 mb-4" />
          <div className="space-y-4">
            <SkeletonUsageBar />
            <SkeletonUsageBar />
            <SkeletonUsageBar />
          </div>
        </div>
      </div>

      {/* Top Campaigns Table */}
      <div>
        <Skeleton className="h-6 w-48 mb-4" />
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <SkeletonTable rows={3} />
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <Skeleton className="h-6 w-32 mb-4" />
        <SkeletonRecentActivity count={10} />
      </div>
    </div>
  );
};

/**
 * Campaign list skeleton - complete loading state for campaign list page
 */
export const SkeletonCampaignList: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-40" />
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Skeleton className="h-4 w-16 mb-2" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div>
            <Skeleton className="h-4 w-16 mb-2" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      </div>

      {/* Campaign Cards/Table */}
      <div className="space-y-4">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    </div>
  );
};

/**
 * Statistics page skeleton - complete loading state for statistics page
 */
export const SkeletonStatistics: React.FC = () => {
  return (
    <div className="p-6 space-y-6">
      {/* Header with date range selector */}
      <div className="flex items-center justify-between">
        <div>
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-10 w-32" />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <SkeletonStatCard />
        <SkeletonStatCard />
        <SkeletonStatCard />
        <SkeletonStatCard />
      </div>

      {/* Line Chart - Daily Traffic */}
      <div className="bg-white shadow rounded-lg p-6">
        <Skeleton className="h-6 w-48 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>

      {/* Two Charts in Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <SkeletonPieChart />

        {/* Bar Chart */}
        <div className="bg-white shadow rounded-lg p-6">
          <Skeleton className="h-6 w-48 mb-4" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>

      {/* Top Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white shadow rounded-lg p-6">
          <Skeleton className="h-6 w-32 mb-4" />
          <SkeletonTable rows={5} />
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <Skeleton className="h-6 w-32 mb-4" />
          <SkeletonTable rows={5} />
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <Skeleton className="h-6 w-32 mb-4" />
          <SkeletonTable rows={5} />
        </div>
      </div>
    </div>
  );
};

export default Skeleton;
